import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Image } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/transitions.css'; // We'll create this file next

// Logo styling
const logoStyle = {
  height: '45px',
  marginBottom: '20px'
};

// Page container style for centering
const pageContainerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '-7vh' // Move content higher on the page
};

const ContactSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  const [contactId, setContactId] = useState('');
  const [error, setError] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [processingIdParam, setProcessingIdParam] = useState(false);
  
  // Check for URL parameters when component mounts or location changes
  useEffect(() => {
    console.log("ContactSearch: Location or auth state changed", { 
      path: location.pathname,
      search: location.search,
      isAuthenticated,
      processingIdParam
    });
    
    // Only process if fully authenticated and not already processing
    if (processingIdParam) {
      console.log("Already processing an ID parameter, skipping");
      return;
    }
    
    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    const idParam = searchParams.get('id');
    
    // Create a flag to track if this effect instance is still current
    let isMounted = true;
    
    const processContactId = () => {
      if (!isMounted) return;

      if (idParam) {
        console.log("Found ID parameter in URL:", idParam);
        setProcessingIdParam(true);
        
        if (!isAuthenticated) {
          console.log("User not authenticated. Storing ID and redirecting to login");
          localStorage.setItem('pendingContactId', idParam);
          localStorage.setItem('redirectUrl', window.location.href);
          
          // Navigate to login (authentication will redirect back after login)
          navigate('/login');
        } else {
          console.log("User is authenticated. Processing ID parameter");
          setContactId(idParam);
          
          // Auto-submit immediately without delay
          console.log("Auto-submitting ID parameter");
          submitContact(idParam);
          
          // Clear the ID parameter from the URL to prevent re-processing
          window.history.replaceState({}, document.title, '/');
        }
      } else if (isAuthenticated) {
        // Check if there's a stored ID from before login
        const storedId = localStorage.getItem('pendingContactId');
        if (storedId) {
          console.log("Found stored contact ID:", storedId);
          setContactId(storedId);
          setProcessingIdParam(true);
          
          // Auto-submit immediately without delay
          console.log("Auto-submitting stored ID");
          submitContact(storedId);
          
          // Clear stored values
          localStorage.removeItem('pendingContactId');
          localStorage.removeItem('redirectUrl');
        }
      }
    };
    
    // Only process when authentication is stable and ready
    if (isAuthenticated !== null && isAuthenticated !== undefined) {
      // Process immediately without delay
      processContactId();
      
      return () => {
        isMounted = false;
      };
    }
    
    // Short delay to ensure DOM is ready for page animations
    const animationTimer = setTimeout(() => {
      if (isMounted) {
        setPageLoaded(true);
      }
    }, 100);
    
    return () => {
      clearTimeout(animationTimer);
      isMounted = false;
    };
  }, [location, isAuthenticated, navigate]);
  
  // Helper function to submit contact ID
  const submitContact = (id) => {
    if (!id || !id.trim()) {
      setError('Please enter a contact ID');
      setProcessingIdParam(false);
      return;
    }
    
    console.log("Submitting contact ID:", id);
    setError(null);
    
    // Navigate directly to contact detail page without loading state or delay
    navigate(`/contact/${id.trim()}`);
    
    // Reset processing flag
    setProcessingIdParam(false);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    submitContact(contactId);
  };
  
  return (
    <div className={`fade-in ${pageLoaded ? 'active' : ''}`} style={pageContainerStyle}>
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6} className="text-center">
            
            <Card className="shadow-lg border-0 slide-in-right">
              
              <Card.Body className="p-4">
                {error && (
                  <Alert variant="danger" className="slide-in-down">
                    {error}
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit} className="fade-in">
                  <Image 
                    src="/crestmontlogo.webp" 
                    alt="Crestmont Logo" 
                    style={logoStyle} 
                    className="mx-auto"
                  />
                  <Form.Group className="mb-4 text-start">
                    <Form.Label>HubSpot Contact ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter contact ID"
                      value={contactId}
                      onChange={(e) => setContactId(e.target.value)}
                      className="form-control-lg"
                    />
                    
                  </Form.Group>
                  
                  <div className="d-grid mt-4">
                    <Button
                      variant="primary"
                      type="submit"
                      className="d-flex justify-content-center align-items-center py-2"
                      size="lg"
                    >
                      <span className="pulse-on-hover">Search Contact</span>
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ContactSearch; 